<template>
  <div>
    <RamadanHeader v-if="ramadanIsActive" />
    <WizardStep
      ref="step"
      :context="context"
      @next="onNext"
      @previous="onPrevious"
      @errorPageClickWhatsApp="onErrorPageClickWhatsApp"
      @errorPageClickCallUs="onErrorPageClickCallUs"
      @error="onError"
      :logoAffinity="logoAffinity"
      v-if="context"
      :page="page"
    >
      <template #default="slotProps">
        <component
          ref="page"
          :params="pageParams"
          :is="slotProps.component"
          :key="slotProps.component"
          v-on="slotProps.events"
          @loading="onLoading"
        />
      </template>

      <template #loading-post-content>
        <div class="loading-message">{{ $t('renew.loader.text') }}</div>
      </template>
    </WizardStep>
  </div>
</template>

<script>
import MotorRenewWizard from '@/wizard/MotorRenewWizard'
import MotorChangePlan from '@/components/renewMotorWizardPages/MotorChangePlan'
import MotorPolicyInfo from '@/components/renewMotorWizardPages/MotorPolicyInfo/MotorPolicyInfo.vue'
import Quote from '@/components/renewMotorWizardPages/Quote'
import MotorVehicleValue from '@/components/renewMotorWizardPages/MotorVehicleValue'
import SelectPlan from '@/components/renewMotorWizardPages/SelectPlan'
import MotorUserDob from '@/components/renewMotorWizardPages/MotorUserDob'
import QuoteReference from '@/components/renewMotorWizardPages/QuoteReference.vue'
import { WizardStep, wizardMixin } from 'qic-components'
import { trackGtag } from '@/utils/useGtag'
import { globalErrorForMetrics } from '@/store/globalErrorForMetrics'
import analyticsDataMixin from '@/mixins/analyticsDataMixin'
import RamadanHeader from '@/components/campaigns/Ramadan/ui/RamadanHeader.vue'
import { mapMutations, mapState } from 'vuex'
import { fetchConfig } from '@/fetchConfig'

export default {
  name: 'WizardRenewMotor',
  props: [
    'page',
    'pageParams'
  ],
  mixins: [
    wizardMixin(MotorRenewWizard),
    analyticsDataMixin
  ],
  components: {
    RamadanHeader,
    WizardStep,
    MotorChangePlan,
    MotorPolicyInfo,
    Quote,
    MotorVehicleValue,
    SelectPlan,
    MotorUserDob,
    QuoteReference
  },
  provide: {
    needHelpWhatsapNumber: 97451047909
  },
  data () {
    return {
      logoAffinity: '',
      showLoadingMessage: false
    }
  },
  methods: {
    ...mapMutations([
      'setRamadanIsActive',
      'setRamadanExpanded'
    ]),
    onNext ({ next, replace }) {
      if (replace) {
        this.$router.replace({ name: 'WizardRenewMotor', params: { page: next }, query: this.$route.query })
        return
      }
      this.$router.push({ name: 'WizardRenewMotor', params: { page: next }, query: this.$route.query })
    },
    onPrevious () {
      // if (this.$refs.page.onWizardBack) this.$refs.page.onWizardBack()
      this.$router.go(-1)
    },
    getAffinityData () {
      const affinityName = this.$route.query.affinity
      if (affinityName) {
        this.$store.dispatch('getAffinityData', affinityName).then(value => {
          this.logoAffinity = value.logo
          if (value.promoCode) {
            this.$store.commit('setPromocode', value.promoCode)
          }
        })
      }
    },
    onErrorPageClickWhatsApp () {
      trackGtag('click_whatsapp', {
        type: 'error_page',
        title: 'Something went wrong',
        text: 'Please try again or contact the support team if the problem persists',
        policyID: this.$store.state.renew.userData?.policy || this.$store.state.renew.policyInfo?.policyNo,
        qid: this.$store.state.renew.userData.qid,
        prev_page: window.location.href
      })
    },
    onErrorPageClickCallUs () {
      trackGtag('click_callus', {
        type: 'error_page',
        title: 'Something went wrong',
        text: 'Please try again or contact the support team if the problem persists',
        policyID: this.$store.state.renew.userData?.policy || this.$store.state.renew.policyInfo?.policyNo,
        qid: this.$store.state.renew.userData.qid,
        prev_page: window.location.href
      })
    },
    onError () {
      trackGtag('error_page', {
        title: 'Something went wrong',
        text: 'Please try again or contact the support team if the problem persists',
        policyID: this.$store.state.renew.userData?.policy || this.$store.state.renew.policyInfo?.policyNo,
        qid: this.$store.state.renew.userData.qid,
        prev_page: window.location.href,
        error_code: globalErrorForMetrics.error_code,
        error_message: globalErrorForMetrics.error_message
      })
    },
    onLoading () {
      this.showLoadingMessage = this.$refs.step.loading === 0

      if (this.$refs.step.loading === 1) {
        const params = this.getAnalyticsData()

        trackGtag('view_page', {
          page: this.$refs.step.page,
          ...params
        })
      }
    },
    async checkRamadan () {
      const ramadanIsActive = await fetchConfig(['motor_qatar_renew_ramadan_2025'])
      this.setRamadanIsActive(ramadanIsActive)

      try {
        const landingLS = localStorage.getItem('qic-qatar-travel.ramadan.bannerIsClosed')
        if (landingLS) {
          const landingHeaderIsClosed = JSON.parse(landingLS)
          this.setRamadanExpanded(!landingHeaderIsClosed)
        }
      } catch (error) {
        console.info(error)
      }
    }
  },
  computed: {
    ...mapState({
      ramadanIsActive: state => state.ramadan.ramadanIsActive
    })
  },
  mounted () {
    this.checkRamadan()
    this.getAffinityData()
  },
  beforeRouteEnter (to, _from, next) {
    const page = to.params?.page
    const nextParams = {}

    if (!MotorRenewWizard.names.includes(page)) {
      nextParams.name = 'NotFound'
      nextParams.params = { pathMatch: to.path.substring(1).split('/') }
      nextParams.query = to.query
      nextParams.hash = to.hash
    }

    return next(nextParams)
  }
}
</script>

<style>
.rtl button {
  direction: rtl !important;
}

.rtl button svg {
  transform: rotate(180deg);
}

.loading-message {
  position: absolute;
  bottom: 20%;
  width: 100%;
  right: 0;
  left: 0;

  display: flex;
  justify-content: center;
}
</style>
